/**
 * Auth sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  login,
  signup,
  getProfile,
  updateProfile,
  setAuthToken,
  setUser,
  setAuthLoading,
  setAuthError,
} from './authSlice';
import { presentLoader, dismissLoader } from './commonSlice';

/**
 * Login saga
 */
function* loginSaga(action: ReturnType<typeof login>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setAuthLoading(true));
    const { email, password } = action.payload;

    const response = yield call(httpRequest.post, endPoints.login, {
      email,
      password,
    });

    // API returns { success, data: { user, token, fcmTokens }, message }
    const data = response?.data ?? response;
    const token = data?.token ?? response?.token;
    const user = data?.user ?? response?.user;

    if (response?.success && token) {
      yield put(setAuthToken(token));
      yield put(
        setUser({
          _id: user?._id ?? response._id,
          username: user?.username ?? response.username,
          email: user?.email ?? response.email,
          picture: user?.picture ?? response.picture,
        })
      );

      toast.success("welcome back");
    } else {
      toast.error('Login failed. Please try again.');
      yield put(setAuthError('Login failed. Please try again.'));
    }
  } catch (error: any) {
    // Extract error message from backend response
    // http-service returns error as { message: "..." } or Error object
    let message = 'Invalid email or password';
    
    if (error?.message) {
      // Error from http-service interceptor (already extracted)
      message = error.message;
    } else if (error?.response?.data?.message) {
      // Direct axios error
      message = error.response.data.message;
    } else if (typeof error === 'string') {
      message = error;
    }
    
    toast.error(message);
    yield put(setAuthError(message));
  } finally {
    yield put(setAuthLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Signup saga
 */
function* signupSaga(action: ReturnType<typeof signup>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setAuthLoading(true));
    const { username, email, password } = action.payload;

    const response = yield call(httpRequest.post, endPoints.signup, {
      email,
      password,
      username,
    });

    // API may return { success, data: { user, token } } or flat structure
    const signupData = response?.data ?? response;
    const signupToken = signupData?.token ?? response?.token;
    const signupUser = signupData?.user ?? response?.user;

    if (response?.success && signupToken) {
      yield put(setAuthToken(signupToken));
      yield put(
        setUser({
          _id: signupUser?._id ?? response._id,
          username: signupUser?.username ?? response.username,
          email: signupUser?.email ?? response.email,
          picture: signupUser?.picture ?? response.picture,
        })
      );

      toast.success("account is created");
    } else {
      toast.error('Something went wrong. Please try again.');
      yield put(setAuthError('Signup failed. Please try again.'));
    }
  } catch (error: any) {
    const message = error?.message || 'Signup failed. Please try again.';
    toast.error(message);
    yield put(setAuthError(message));
  } finally {
    yield put(setAuthLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Profile saga
 */
function* profileSaga(): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setAuthLoading(true));

    const response = yield call(httpRequest.get, endPoints.profile, {});

    if (response?.success) {
      yield put(setUser(response.data || response));
    } else {
      toast.error('Unable to fetch profile. Please try again.');
      yield put(setAuthError('Unable to fetch profile. Please try again.'));
    }
  } catch (error: any) {
    const message = error?.message || 'Unable to fetch profile. Please try again.';
    toast.error(message);
    yield put(setAuthError(message));
  } finally {
    yield put(setAuthLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update Profile saga
 */
function* updateProfileSaga(action: ReturnType<typeof updateProfile>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setAuthLoading(true));

    const response = yield call(httpRequest.put, endPoints.updateProfile, action.payload);

    if (response?.success) {
      yield put(setUser(response.data || response));
      toast.success('profile is updated');
    } else {
      toast.error('Unable to update profile. Please try again.');
      yield put(setAuthError('Unable to update profile. Please try again.'));
    }
  } catch (error: any) {
    const message = error?.message || 'Unable to update profile. Please try again.';
    toast.error(message);
    yield put(setAuthError(message));
  } finally {
    yield put(setAuthLoading(false));
    yield put(dismissLoader());
  }
}

export function* authSagas(): Generator<any, void, any> {
  yield takeLatest(login, loginSaga);
  yield takeLatest(signup, signupSaga);
  yield takeLatest(getProfile, profileSaga);
  yield takeLatest(updateProfile, updateProfileSaga);
}

